import { type GridPosition } from '../../machine/state/types';
import { isSingleCellSelection } from '../highlights/utils/is-single-cell-selection';
import { createEffect } from '../utils/create-effect';

export interface SelectionsPingEffectDeps {
  /**
   * Called on every selection change. Notifies the ping registry on the static
   * `database::selectionChanged` channel so that broadcast-level subscribers
   * (action bar, table layout, etc.) re-read aggregate state.
   */
  notifyDatabaseSelectionChanged: () => void;

  resolveCellPositionToIds(position: GridPosition): {
    entryId: string | undefined;
    fieldId: string | undefined;
  };

  /**
   * Invoked with `selected: true` on every qualifying delta so subscribers
   * (e.g. scroll-into-view) can react without mirroring selection state.
   */
  notifyCellSelected: (entryId: string, fieldId: string, selected: boolean) => void;
}

/**
 * Used to notify coarse subscriptions to any selections changes.
 * This should be used sparingly as this acts as our bridge today
 * to communicate with some react components that are NOT widely used
 * in the way that table layout cells are.
 */
export const createSelectionsPingEffect = createEffect(
  ({
    notifyDatabaseSelectionChanged,
    resolveCellPositionToIds,
    notifyCellSelected,
  }: SelectionsPingEffectDeps) => {
    return {
      deltaTypes: ['selectionReplaced', 'selectionModified'] as const,

      onDelta(_delta, state) {
        notifyDatabaseSelectionChanged();

        const isActiveSingleCell =
          state.mode === 'cell' &&
          state.selections.length === 1 &&
          isSingleCellSelection(state.selections[0], state.mode);

        if (!isActiveSingleCell) {
          return;
        }

        const { entryId, fieldId } = resolveCellPositionToIds(state.selections[0].anchor);

        if (entryId === undefined || fieldId === undefined) {
          return;
        }

        notifyCellSelected(entryId, fieldId, true);
      },
    };
  }
);
